<template>
  <div
    class="d-flex align-items-start p-3 border-bottom"
    :class="{ 'bg-light text-muted': !item.isAvailable }"
    style="min-width: 0;"
  >
    <a :href="'/listing/' + item.productId" class="mr-2 mr-sm-3 flex-shrink-0" style="text-decoration:none;">
      <img
        :src="(storageUrl || '').replace(/\/+$/, '') + '/' + item.primaryImageFileName"
        :alt="item.productTitle"
        class="rounded"
        style="width: 64px; height: 64px; object-fit: cover;"
      />
    </a>
    <div class="flex-grow-1" style="min-width: 0;">
      <div class="d-flex justify-content-between">
        <div style="min-width:0;flex:1 1 auto;padding-right:10px;">
          <a :href="'/listing/' + item.productId" class="text-dark font-weight-bold d-block" style="text-decoration:none;">
            {{ item.productTitle }}
          </a>
          <!-- What one purchase contains, and — when several are ordered — the arithmetic
               spelled out. "Qty 2" beside "Includes 2" is four animals, and leaving the buyer
               to work that out about livestock is exactly the confusion this feature would
               otherwise create. -->
          <div v-if="item.composition" class="small mt-1">
            <span style="color:#8a3f6d;background:#f3e7ee;border-radius:3px;padding:1px 6px;display:inline-block;">
              {{ item.composition }}
            </span>
            <strong v-if="item.compositionTotal" class="ml-1 text-dark">
              {{ item.compositionTotal }}
            </strong>
          </div>
          <small v-if="!item.isAvailable" class="text-danger">This listing is no longer available</small>
          <!-- One chip, with the direction: a drop is good news, not a warning. -->
          <span
            v-if="item.priceChanged"
            class="d-inline-block mt-1"
            :style="item.unitPrice < item.priceAtTimeOfAdd
              ? 'background:#e7f7eb;color:#1a6b34;border-radius:4px;padding:1px 6px;font-size:.75rem;font-weight:700;'
              : 'background:#fff6e0;color:#8a5a00;border-radius:4px;padding:1px 6px;font-size:.75rem;font-weight:700;'"
          >
            {{ item.unitPrice < item.priceAtTimeOfAdd ? 'Price dropped' : 'Price went up' }}
            ${{ item.priceAtTimeOfAdd.toFixed(2) }} &rarr; ${{ item.unitPrice.toFixed(2) }}
          </span>
          <div v-if="item.isOnSale" class="mt-1">
            <span class="text-danger font-weight-bold">${{ item.unitPrice.toFixed(2) }}</span>
            <span class="text-muted ml-1"><s>${{ item.originalPrice.toFixed(2) }}</s></span>
          </div>
          <div v-else class="mt-1">
            <span class="font-weight-bold">${{ item.unitPrice.toFixed(2) }}</span>
          </div>
        </div>
        <!-- The item's own price only. Shipping is shown once per shop, with how it adds up:
             adding each item's shipping here made a $23 trio read "$46.00" with no explanation. -->
        <div class="text-right" style="flex:0 0 auto;">
          <strong>${{ (item.unitPrice * item.quantity).toFixed(2) }}</strong>
        </div>
      </div>

      <div class="d-flex flex-wrap align-items-center mt-2">
        <div class="mr-2 mb-1">
          <label class="small text-muted mb-0">Qty:</label>
          <select
            class="form-control form-control-sm d-inline-block ml-1"
            style="width: 55px;"
            :value="item.quantity"
            @change="$emit('update-quantity', parseInt($event.target.value))"
            :disabled="!item.isAvailable"
          >
            <option v-for="n in Math.max(item.maxQuantity, item.quantity)" :key="n" :value="n">{{ n }}</option>
          </select>
        </div>

        <div class="mr-2 mb-1">
          <label class="small text-muted mb-0">Ship:</label>
          <select
            class="form-control form-control-sm d-inline-block ml-1"
            style="width: auto; max-width: 190px;"
            :value="item.shippingTypeId"
            @change="$emit('update-shipping', parseInt($event.target.value))"
            :disabled="!item.isAvailable"
          >
            <option v-if="item.hasStandardShipping" value="0">{{ shipLabel('Standard', item.standardShippingPrice) }}</option>
            <option v-if="item.hasExpeditedShipping" value="1">{{ shipLabel('Expedited', item.expeditedShippingPrice) }}</option>
            <option v-if="item.hasNextDayShipping" value="2">{{ shipLabel('Next Day', item.nextDayShippingPrice) }}</option>
            <!-- Local pickup sunset 2026-05-01: only show when it's the ONLY option,
                 so legacy pickup-only listings remain checkoutable. -->
            <option v-if="pickupOnly" value="3">Local Pickup</option>
          </select>
        </div>
        <!-- Pickup-only lines say where, before anyone pays: a buyer far from the seller would
             otherwise pay for fish they can't collect. -->
        <div v-if="pickupOnly" class="small text-muted w-100 mb-1">
          <i class="fas fa-store mr-1"></i>Pickup only{{ item.localPickupLocation ? ' in ' + item.localPickupLocation : '' }}. This item doesn't ship.
        </div>

        <button
          class="btn btn-sm btn-link text-danger p-0 ml-auto mb-1"
          @click="$emit('remove')"
          title="Remove"
        >
          Remove
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    item: Object,
    storageUrl: String
  },
  computed: {
    // Nothing ships, so the only choice is pickup (Local pickup sunset 2026-05-01).
    pickupOnly() {
      var i = this.item;
      return i.hasLocalPickup && !i.hasStandardShipping && !i.hasExpeditedShipping && !i.hasNextDayShipping;
    }
  },
  methods: {
    // "Expedited · $23", "Expedited · included". The listing's own price for that option;
    // the shop row below says how the shop combines them.
    shipLabel(name, price) {
      if (price === null || price === undefined) return name;
      return name + ' · ' + (price === 0 ? 'included' : '$' + (price % 1 === 0 ? price.toFixed(0) : price.toFixed(2)));
    }
  }
};
</script>
